import React, { useState, useCallback } from 'react';
import { MessageSquare, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { VoiceNoteButton } from '@/components/lead-detail/VoiceNoteButton';
import { VoiceInsightsCard } from '@/components/lead-detail/VoiceInsightsCard';
import { useOfflineQueue } from '@/hooks/useOfflineQueue';
import api from '@/lib/api';

// Fallback: tomorrow 9:00 AM in the user's local timezone
function defaultDateTime() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return { date: `${yyyy}-${mm}-${dd}`, time: '09:00' };
}

export function LeadNotesSection({ notes, isViewer, note, setNote, onAddNote, submitting, lead }) {
  const { online } = useOfflineQueue();
  const [insights, setInsights] = useState(null);
  const [scheduledItems, setScheduledItems] = useState(new Set());
  const [schedulingIdx, setSchedulingIdx] = useState(null);

  const context = lead ? `Lead: ${lead.name}${lead.company ? ' at ' + lead.company : ''}${lead.status ? ' (status: ' + lead.status + ')' : ''}` : null;

  const handleVoiceResult = (result) => {
    setInsights(result);
    setScheduledItems(new Set());
  };

  const insertAndClearInsights = (text) => {
    setNote((prev) => (prev ? `${prev}\n\n${text}` : text));
    setInsights(null);
    setScheduledItems(new Set());
  };

  const handleScheduleActionItem = useCallback(
    async (item, idx) => {
      if (!item?.task) return;
      setSchedulingIdx(idx);
      const fallback = defaultDateTime();
      const payload = {
        title: item.task.slice(0, 120),
        description: insights?.summary
          ? `Auto-created from voice note.\n\nSummary: ${insights.summary}`
          : 'Auto-created from voice note.',
        date: item.due_date || fallback.date,
        time: item.due_time || (item.due_date ? '09:00' : fallback.time),
        duration: 30,
        lead_id: lead?.id || null,
        attendees: [],
        reminder_minutes: 30,
        recurrence: 'none',
        shared_with_org: false,
        lead_reminder_hours: 0,
      };
      try {
        await api.createAppointment(payload);
        setScheduledItems((prev) => {
          const next = new Set(prev);
          next.add(idx);
          return next;
        });
        toast.success(`Scheduled: ${item.task.slice(0, 60)}`, {
          description: `${payload.date} at ${payload.time}`,
        });
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || 'Failed to schedule';
        toast.error('Could not create appointment', { description: msg });
      } finally {
        setSchedulingIdx(null);
      }
    },
    [insights, lead],
  );

  return (
    <div className="border border-slate-200 rounded-sm bg-slate-50/60" data-testid="lead-notes-section">
      <div className="p-4 border-b border-slate-200 flex items-center gap-2 bg-slate-100">
        <MessageSquare className="w-4 h-4 text-slate-500" />
        <h2 className="font-heading font-semibold text-slate-700 text-sm">Notes ({notes?.length || 0})</h2>
      </div>
      {!isViewer && (
        <div className="p-4 border-b border-slate-200">
          {insights && (
            <VoiceInsightsCard
              transcript={insights.transcript}
              summary={insights.summary}
              actionItems={insights.action_items || []}
              onInsert={insertAndClearInsights}
              onSchedule={handleScheduleActionItem}
              scheduledItems={scheduledItems}
              schedulingIdx={schedulingIdx}
              onDismiss={() => { setInsights(null); setScheduledItems(new Set()); }}
            />
          )}
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note…"
            className="rounded-sm mb-2"
            rows={3}
            data-testid="note-input"
          />
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={onAddNote}
              disabled={submitting || !note.trim()}
              className="bg-black hover:bg-slate-800 text-white rounded-sm text-sm"
              data-testid="add-note-btn"
            >
              <Send className="w-3.5 h-3.5 mr-1" /> {submitting ? 'Adding…' : 'Add Note'}
            </Button>
            <VoiceNoteButton online={online} context={context} onResult={handleVoiceResult} />
          </div>
        </div>
      )}
      <div className="p-4 space-y-4">
        {notes?.length > 0 ? notes.slice().reverse().map((n, i) => (
          <div key={n.id || `note-${i}`} className="flex gap-3" data-testid={`note-${i}`}>
            <div className="w-7 h-7 bg-slate-100 rounded-sm flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-xs font-medium text-slate-600">{n.author?.[0]?.toUpperCase()}</span>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium text-slate-900">{n.author}</span>
                <span className="text-xs text-slate-400">{new Date(n.created_at).toLocaleString()}</span>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{n.content}</p>
            </div>
          </div>
        )) : (
          <p className="text-sm text-slate-400 text-center py-4">No notes yet</p>
        )}
      </div>
    </div>
  );
}
